import { parseISO, format, formatDistanceToNow } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { isSupportedCountry, isValidPhoneNumber as isValidPhoneNumber$1, parsePhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import i18n from 'i18next';
export { default as i18n } from 'i18next';
import { initReactI18next } from 'react-i18next';
export { I18nextProvider, Trans, useTranslation } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { Country, State } from 'country-state-city';

// src/formatters.ts
function formatPhoneNumber(phone) {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
}
function formatE164(phone, defaultCountryCode = "1") {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `+${defaultCountryCode}${cleaned}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `+${cleaned}`;
  }
  if (!cleaned.startsWith("+")) {
    return `+${cleaned}`;
  }
  return cleaned;
}
function formatDate(date, formatString = "MMM d, yyyy", timezone) {
  const d = typeof date === "string" ? parseISO(date) : date;
  if (timezone) {
    return formatInTimeZone(d, timezone, formatString);
  }
  return format(d, formatString);
}
function buildCampaignName(label, date = /* @__PURE__ */ new Date()) {
  return `${label} - ${formatDate(date)}`;
}
function formatDateTime(date, formatString = "MMM d, yyyy h:mm a", timezone) {
  return formatDate(date, formatString, timezone);
}
function formatRelativeTime(date) {
  const d = typeof date === "string" ? parseISO(date) : date;
  return formatDistanceToNow(d, { addSuffix: true });
}
function formatCurrency(amount, currency = "USD", locale = "en-US") {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency
  }).format(amount);
}
function formatNumber(num, locale = "en-US") {
  return new Intl.NumberFormat(locale).format(num);
}
function formatPercentage(value, decimals = 1) {
  return `${value.toFixed(decimals)}%`;
}
function formatFileSize(bytes) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
function formatFullName(firstName, lastName) {
  return lastName ? `${firstName} ${lastName}` : firstName;
}
function getInitials(firstName, lastName) {
  const first = firstName.charAt(0).toUpperCase();
  const last = lastName?.charAt(0).toUpperCase() || "";
  return `${first}${last}`;
}
function truncate(text, maxLength) {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
}
function calculateSmsSegments(text) {
  const gsmRegex = (
    // eslint-disable-next-line no-control-regex, no-useless-escape
    /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\x1B !"#¤%&'()*+,\-.\/0-9:;<=>?¡A-ZÄÖÑÜa-zäöñüà]*$/
  );
  const isGsm = gsmRegex.test(text);
  const encoding = isGsm ? "GSM" : "Unicode";
  const maxCharsPerSegment = isGsm ? 160 : 70;
  const maxCharsMultiSegment = isGsm ? 153 : 67;
  const characters = text.length;
  let segments;
  if (characters <= maxCharsPerSegment) {
    segments = 1;
  } else {
    segments = Math.ceil(characters / maxCharsMultiSegment);
  }
  return { segments, characters, encoding };
}
function slugify(text) {
  return text.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/[\s_-]+/g, "-").replace(/^-+|-+$/g, "");
}
function isValidPhoneNumber(phone, countryCode) {
  if (!phone) return false;
  try {
    if (countryCode && isSupportedCountry(countryCode)) {
      return isValidPhoneNumber$1(phone, countryCode);
    }
    try {
      const parsed = parsePhoneNumber(phone);
      return parsed?.isValid() ?? false;
    } catch {
    }
  } catch {
  }
  const cleaned = phone.replace(/\D/g, "");
  return cleaned.length >= 7 && cleaned.length <= 15;
}
function normalizeToE164(raw, defaultCountry = "US") {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return { ok: false, reason: "empty" };
  const parsed = parsePhoneNumberFromString(
    trimmed,
    trimmed.startsWith("+") ? void 0 : defaultCountry
  );
  if (!parsed || !parsed.isValid()) return { ok: false, reason: "invalid" };
  return { ok: true, e164: parsed.number };
}
function toSmsRoutingKey(raw) {
  const normalized = normalizeToE164(raw);
  if (normalized.ok) return normalized.e164;
  const digits = (raw ?? "").replace(/\D/g, "");
  if (!digits) return null;
  return digits.length === 10 ? `+1${digits}` : `+${digits}`;
}
function phoneMatchVariants(e164) {
  const variants = /* @__PURE__ */ new Set([e164]);
  const digits = e164.replace(/\D/g, "");
  variants.add(digits);
  if (digits.length === 11 && digits.startsWith("1")) {
    const n = digits.slice(1);
    const [a, b, c] = [n.slice(0, 3), n.slice(3, 6), n.slice(6)];
    variants.add(n);
    variants.add(`${a}-${b}-${c}`);
    variants.add(`(${a}) ${b}-${c}`);
    variants.add(`+1 ${a} ${b} ${c}`);
    variants.add(`+1-${a}-${b}-${c}`);
    variants.add(`+1 (${a}) ${b}-${c}`);
    variants.add(`1${n}`);
  }
  return [...variants];
}
function toStoredPhone(raw) {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return null;
  const normalized = normalizeToE164(trimmed);
  return normalized.ok ? normalized.e164 : trimmed;
}
function storedPhoneVariants(raw) {
  const trimmed = (raw ?? "").trim();
  const key = toSmsRoutingKey(trimmed);
  if (!key) return [];
  return [.../* @__PURE__ */ new Set([...phoneMatchVariants(key), trimmed])];
}

// src/validators.ts
function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}
function isValidPostalCode(code, country) {
  const v = code.trim();
  if (!v) return false;
  switch ((country ?? "").toUpperCase()) {
    case "US":
      return /^\d{5}(-\d{4})?$/.test(v);
    case "CA":
      return /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(v);
    case "GB":
      return /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s?\d[A-Za-z]{2}$/.test(v);
    case "AU":
      return /^\d{4}$/.test(v);
    default:
      return /^[A-Za-z0-9][A-Za-z0-9\s-]{0,10}[A-Za-z0-9]$/.test(v);
  }
}
function isValidUrl(url) {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}
function isValidUuid(uuid) {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}
function isValidHexColor(color) {
  return /^#[0-9A-Fa-f]{6}$/.test(color);
}
function containsOptOutKeyword(text) {
  const optOutKeywords = ["stop", "unsubscribe", "cancel", "quit", "end", "optout", "opt-out"];
  const lowered = text.toLowerCase().trim();
  return optOutKeywords.some((keyword) => lowered === keyword || lowered.startsWith(`${keyword} `));
}
function containsOptInKeyword(text) {
  const optInKeywords = ["start", "subscribe", "yes", "optin", "opt-in", "unstop"];
  const lowered = text.toLowerCase().trim();
  return optInKeywords.some((keyword) => lowered === keyword || lowered.startsWith(`${keyword} `));
}
function validatePasswordStrength(password) {
  const errors = [];
  let score = 0;
  if (password.length < 8) {
    errors.push("Password must be at least 8 characters");
  } else {
    score += 1;
  }
  if (!/[a-z]/.test(password)) {
    errors.push("Password must contain a lowercase letter");
  } else {
    score += 1;
  }
  if (!/[A-Z]/.test(password)) {
    errors.push("Password must contain an uppercase letter");
  } else {
    score += 1;
  }
  if (!/\d/.test(password)) {
    errors.push("Password must contain a number");
  } else {
    score += 1;
  }
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push("Password must contain a special character");
  } else {
    score += 1;
  }
  return {
    isValid: errors.length === 0,
    errors,
    score
  };
}
function sanitizeHtml(html) {
  return html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}
function isWithinTcpaHours(hour) {
  return hour >= 8 && hour < 21;
}

// src/constants.ts
var SUPPORTED_LANGUAGES = ["en", "es", "fr"];
var LANGUAGE_NAMES = {
  en: "English",
  es: "Espa\xF1ol",
  fr: "Fran\xE7ais"
};
var US_TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" }
];
var COLORS = {
  primary: {
    50: "#eff6ff",
    100: "#dbeafe",
    200: "#bfdbfe",
    300: "#93c5fd",
    400: "#60a5fa",
    500: "#3b82f6",
    600: "#2563eb",
    700: "#1d4ed8",
    800: "#1e40af",
    900: "#1e3a8a"
  },
  neutral: {
    50: "#fafafa",
    100: "#f5f5f5",
    200: "#e5e5e5",
    300: "#d4d4d4",
    400: "#a3a3a3",
    500: "#737373",
    600: "#525252",
    700: "#404040",
    800: "#262626",
    900: "#171717"
  },
  semantic: {
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    info: "#3b82f6"
  }
};
var TAG_COLORS = [
  "#3b82f6",
  // Blue
  "#10b981",
  // Green
  "#f59e0b",
  // Yellow
  "#ef4444",
  // Red
  "#8b5cf6",
  // Purple
  "#ec4899",
  // Pink
  "#06b6d4",
  // Cyan
  "#f97316",
  // Orange
  "#6366f1",
  // Indigo
  "#84cc16"
  // Lime
];
var STATUS_COLORS = {
  pending: "#f59e0b",
  sent: "#3b82f6",
  delivered: "#10b981",
  opened: "#8b5cf6",
  signed: "#10b981",
  failed: "#ef4444",
  expired: "#6b7280",
  cancelled: "#6b7280"
};
var SEND_TYPES = {
  message: { label: "Message", icon: "MessageSquare" },
  document: { label: "Document", icon: "FileText" },
  form: { label: "Form", icon: "ClipboardList" },
  voice: { label: "Voice", icon: "Phone" }
};
var DELIVERY_METHODS = {
  sms: { label: "SMS", icon: "Smartphone" },
  email: { label: "Email", icon: "Mail" },
  voice: { label: "Voice", icon: "PhoneCall" }
};
var SMS_LIMITS = {
  GSM_SINGLE_SEGMENT: 160,
  GSM_MULTI_SEGMENT: 153,
  UNICODE_SINGLE_SEGMENT: 70,
  UNICODE_MULTI_SEGMENT: 67,
  MAX_SEGMENTS: 10
};
var FILE_LIMITS = {
  MAX_FILE_SIZE: 10 * 1024 * 1024,
  // 10MB
  MAX_FILES_PER_UPLOAD: 5,
  ALLOWED_DOCUMENT_TYPES: ["application/pdf"],
  ALLOWED_IMAGE_TYPES: ["image/jpeg", "image/png", "image/gif", "image/webp"]
};
var PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100]
};
var RATE_LIMITS = {
  DEFAULT_REQUESTS_PER_MINUTE: 60,
  BULK_SEND_MAX_RECIPIENTS: 1e3,
  WEBHOOK_MAX_RETRIES: 5
};
var ANIMATION = {
  FAST: 150,
  NORMAL: 200,
  SLOW: 300,
  PAGE_TRANSITION: 200
};
var BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536
};
var STORAGE_KEYS = {
  AUTH_TOKEN: "respectly_auth_token",
  REFRESH_TOKEN: "respectly_refresh_token",
  USER_PREFERENCES: "respectly_user_prefs",
  VIEW_MODE: "respectly_view_mode",
  SIDEBAR_COLLAPSED: "respectly_sidebar_collapsed"
};

// src/i18n/locales/en.ts
var en_default = {
  common: {
    // General
    appName: "EsigForms",
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    add: "Add",
    remove: "Remove",
    close: "Close",
    confirm: "Confirm",
    back: "Back",
    next: "Next",
    previous: "Previous",
    submit: "Submit",
    search: "Search",
    filter: "Filter",
    sort: "Sort",
    clear: "Clear",
    reset: "Reset",
    apply: "Apply",
    view: "View",
    download: "Download",
    upload: "Upload",
    export: "Export",
    import: "Import",
    refresh: "Refresh",
    retry: "Retry",
    yes: "Yes",
    no: "No",
    ok: "OK",
    done: "Done",
    select: "Select",
    selectAll: "Select all",
    deselectAll: "Deselect all",
    noResults: "No results found",
    showMore: "Show more",
    showLess: "Show less",
    // Navigation
    home: "Home",
    dashboard: "Dashboard",
    settings: "Settings",
    profile: "Profile",
    help: "Help",
    logout: "Log out",
    login: "Log in",
    signUp: "Sign up",
    // Status
    status: "Status",
    active: "Active",
    inactive: "Inactive",
    pending: "Pending",
    completed: "Completed",
    failed: "Failed",
    draft: "Draft",
    sent: "Sent",
    delivered: "Delivered",
    opened: "Opened",
    clicked: "Clicked",
    signed: "Signed",
    declined: "Declined",
    expired: "Expired",
    scheduled: "Scheduled",
    processing: "Processing",
    // Time
    today: "Today",
    yesterday: "Yesterday",
    thisWeek: "This week",
    thisMonth: "This month",
    lastMonth: "Last month",
    custom: "Custom",
    dateRange: "Date range",
    from: "From",
    to: "To",
    // Pagination
    page: "Page",
    of: "of",
    showing: "Showing",
    results: "results",
    perPage: "per page",
    firstPage: "First page",
    lastPage: "Last page",
    nextPage: "Next page",
    previousPage: "Previous page",
    // Forms
    required: "Required",
    optional: "Optional",
    invalidEmail: "Invalid email address",
    invalidPhone: "Invalid phone number",
    passwordTooShort: "Password must be at least 8 characters",
    fieldRequired: "This field is required",
    minLength: "Must be at least {{count}} characters",
    maxLength: "Must be at most {{count}} characters",
    // Errors
    error: "Error",
    errorOccurred: "An error occurred",
    tryAgain: "Please try again",
    somethingWentWrong: "Something went wrong",
    networkError: "Network error",
    unauthorized: "Unauthorized",
    forbidden: "Forbidden",
    notFound: "Not found",
    serverError: "Server error",
    // Success
    success: "Success",
    saved: "Saved successfully",
    deleted: "Deleted successfully",
    created: "Created successfully",
    updated: "Updated successfully",
    sentSuccessfully: "Sent successfully",
    // Confirmations
    areYouSure: "Are you sure?",
    cannotUndo: "This action cannot be undone.",
    deleteConfirm: "Are you sure you want to delete this {{item}}?"
  },
  contacts: {
    title: "Contacts",
    contact: "Contact",
    contacts: "Contacts",
    addContact: "Add contact",
    editContact: "Edit contact",
    deleteContact: "Delete contact",
    importContacts: "Import contacts",
    exportContacts: "Export contacts",
    searchContacts: "Search contacts...",
    noContacts: "No contacts yet",
    noContactsDesc: "Add contacts to start sending messages, documents, and forms.",
    // Fields
    firstName: "First name",
    lastName: "Last name",
    email: "Email",
    phone: "Phone",
    company: "Company",
    address: "Address",
    city: "City",
    state: "State",
    zipCode: "ZIP code",
    country: "Country",
    timezone: "Timezone",
    tags: "Tags",
    // Consent
    consent: "Consent",
    smsOptIn: "SMS opt-in",
    emailOptIn: "Email opt-in",
    voiceOptIn: "Voice opt-in",
    optedIn: "Opted in",
    optedOut: "Opted out",
    consentLevel: "Consent level",
    transactional: "Transactional",
    informational: "Informational",
    marketing: "Marketing"
  },
  documents: {
    title: "Documents",
    document: "Document",
    documents: "Documents",
    template: "Template",
    templates: "Templates",
    createTemplate: "Create template",
    editTemplate: "Edit template",
    deleteTemplate: "Delete template",
    noDocuments: "No documents yet",
    noDocumentsDesc: "Create document templates that can be sent for electronic signatures.",
    // Actions
    send: "Send for signature",
    resend: "Resend",
    remind: "Send reminder",
    preview: "Preview",
    duplicate: "Duplicate",
    archive: "Archive",
    // Status
    waitingForSignature: "Waiting for signature",
    partiallySigned: "Partially signed",
    fullySigned: "Fully signed",
    expiresOn: "Expires on {{date}}"
  },
  forms: {
    title: "Forms",
    form: "Form",
    forms: "Forms",
    createForm: "Create form",
    editForm: "Edit form",
    deleteForm: "Delete form",
    noForms: "No forms yet",
    noFormsDesc: "Create intake forms to collect information from your contacts.",
    // Builder
    formBuilder: "Form Builder",
    addField: "Add field",
    fieldSettings: "Field settings",
    fieldTypes: "Field types",
    textField: "Text field",
    emailField: "Email field",
    phoneField: "Phone field",
    numberField: "Number field",
    dateField: "Date field",
    dropdownField: "Dropdown",
    checkboxField: "Checkbox",
    radioField: "Radio buttons",
    fileUpload: "File upload",
    signature: "Signature",
    sectionHeader: "Section header",
    // Settings
    formSettings: "Form settings",
    publicUrl: "Public URL",
    qrCode: "QR Code",
    embedCode: "Embed code",
    submissions: "Submissions",
    viewSubmissions: "View submissions"
  },
  messaging: {
    title: "Messaging",
    messages: "Messages",
    inbox: "Inbox",
    compose: "Compose",
    send: "Send",
    sendNow: "Send now",
    scheduleFor: "Schedule for",
    recipients: "Recipients",
    recipient: "Recipient",
    selectRecipients: "Select recipients",
    // Channels
    sms: "SMS",
    email: "Email",
    voice: "Voice",
    both: "Both",
    // Types
    messageType: "Message type",
    transactional: "Transactional",
    informational: "Informational",
    marketing: "Marketing",
    // Content
    subject: "Subject",
    message: "Message",
    template: "Template",
    selectTemplate: "Select template",
    characterCount: "{{count}} characters",
    segments: "{{count}} segments",
    // Inbox
    noMessages: "No messages yet",
    noMessagesDesc: "When you receive messages, they'll appear here.",
    unread: "Unread",
    markAsRead: "Mark as read",
    markAsUnread: "Mark as unread",
    reply: "Reply",
    assign: "Assign",
    assignTo: "Assign to",
    resolve: "Resolve",
    archive: "Archive"
  },
  activity: {
    title: "Activity",
    recentActivity: "Recent activity",
    allActivity: "All activity",
    filterByType: "Filter by type",
    filterByStatus: "Filter by status",
    filterByDate: "Filter by date",
    noActivity: "No activity yet",
    noActivityDesc: "Your sends and communications will appear here."
  },
  settings: {
    title: "Settings",
    general: "General",
    account: "Account",
    team: "Team",
    billing: "Billing",
    integrations: "Integrations",
    notifications: "Notifications",
    security: "Security",
    apiKeys: "API Keys",
    webhooks: "Webhooks",
    // Team
    teamMembers: "Team members",
    inviteMember: "Invite member",
    role: "Role",
    admin: "Admin",
    member: "Member",
    pending: "Pending",
    resendInvite: "Resend invite",
    removeFromTeam: "Remove from team"
  },
  auth: {
    signIn: "Sign in",
    signUp: "Sign up",
    signOut: "Sign out",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm password",
    forgotPassword: "Forgot password?",
    resetPassword: "Reset password",
    createAccount: "Create account",
    alreadyHaveAccount: "Already have an account?",
    dontHaveAccount: "Don't have an account?",
    orContinueWith: "Or continue with",
    termsAgreement: "By continuing, you agree to our {{terms}} and {{privacy}}.",
    termsOfService: "Terms of Service",
    privacyPolicy: "Privacy Policy"
  },
  tags: {
    title: "Tags",
    tag: "Tag",
    tags: "Tags",
    createTag: "Create tag",
    editTag: "Edit tag",
    deleteTag: "Delete tag",
    tagName: "Tag name",
    tagColor: "Tag color",
    noTags: "No tags yet",
    noTagsDesc: "Create tags to organize your contacts and content.",
    selectTags: "Select tags",
    addTag: "Add tag",
    removeTag: "Remove tag"
  }
};

// src/i18n/locales/es.ts
var es_default = {
  common: {
    // General
    appName: "AORForms",
    loading: "Cargando...",
    save: "Guardar",
    cancel: "Cancelar",
    delete: "Eliminar",
    edit: "Editar",
    create: "Crear",
    add: "Agregar",
    remove: "Quitar",
    close: "Cerrar",
    confirm: "Confirmar",
    back: "Atr\xE1s",
    next: "Siguiente",
    previous: "Anterior",
    submit: "Enviar",
    search: "Buscar",
    filter: "Filtrar",
    sort: "Ordenar",
    clear: "Limpiar",
    reset: "Restablecer",
    apply: "Aplicar",
    view: "Ver",
    download: "Descargar",
    upload: "Subir",
    export: "Exportar",
    import: "Importar",
    refresh: "Actualizar",
    retry: "Reintentar",
    yes: "S\xED",
    no: "No",
    ok: "Aceptar",
    done: "Hecho",
    select: "Seleccionar",
    selectAll: "Seleccionar todo",
    deselectAll: "Deseleccionar todo",
    noResults: "No se encontraron resultados",
    showMore: "Mostrar m\xE1s",
    showLess: "Mostrar menos",
    // Navigation
    home: "Inicio",
    dashboard: "Panel",
    settings: "Configuraci\xF3n",
    profile: "Perfil",
    help: "Ayuda",
    logout: "Cerrar sesi\xF3n",
    login: "Iniciar sesi\xF3n",
    signUp: "Registrarse",
    // Status
    status: "Estado",
    active: "Activo",
    inactive: "Inactivo",
    pending: "Pendiente",
    completed: "Completado",
    failed: "Fallido",
    draft: "Borrador",
    sent: "Enviado",
    delivered: "Entregado",
    opened: "Abierto",
    clicked: "Clic",
    signed: "Firmado",
    declined: "Rechazado",
    expired: "Expirado",
    scheduled: "Programado",
    processing: "Procesando",
    // Time
    today: "Hoy",
    yesterday: "Ayer",
    thisWeek: "Esta semana",
    thisMonth: "Este mes",
    lastMonth: "Mes pasado",
    custom: "Personalizado",
    dateRange: "Rango de fechas",
    from: "Desde",
    to: "Hasta",
    // Pagination
    page: "P\xE1gina",
    of: "de",
    showing: "Mostrando",
    results: "resultados",
    perPage: "por p\xE1gina",
    firstPage: "Primera p\xE1gina",
    lastPage: "\xDAltima p\xE1gina",
    nextPage: "Siguiente p\xE1gina",
    previousPage: "P\xE1gina anterior",
    // Forms
    required: "Requerido",
    optional: "Opcional",
    invalidEmail: "Correo electr\xF3nico inv\xE1lido",
    invalidPhone: "N\xFAmero de tel\xE9fono inv\xE1lido",
    passwordTooShort: "La contrase\xF1a debe tener al menos 8 caracteres",
    fieldRequired: "Este campo es requerido",
    minLength: "Debe tener al menos {{count}} caracteres",
    maxLength: "Debe tener como m\xE1ximo {{count}} caracteres",
    // Errors
    error: "Error",
    errorOccurred: "Ocurri\xF3 un error",
    tryAgain: "Por favor, intente de nuevo",
    somethingWentWrong: "Algo sali\xF3 mal",
    networkError: "Error de red",
    unauthorized: "No autorizado",
    forbidden: "Prohibido",
    notFound: "No encontrado",
    serverError: "Error del servidor",
    // Success
    success: "\xC9xito",
    saved: "Guardado correctamente",
    deleted: "Eliminado correctamente",
    created: "Creado correctamente",
    updated: "Actualizado correctamente",
    sentSuccessfully: "Enviado correctamente",
    // Confirmations
    areYouSure: "\xBFEst\xE1 seguro?",
    cannotUndo: "Esta acci\xF3n no se puede deshacer.",
    deleteConfirm: "\xBFEst\xE1 seguro de que desea eliminar este {{item}}?"
  },
  contacts: {
    title: "Contactos",
    contact: "Contacto",
    contacts: "Contactos",
    addContact: "Agregar contacto",
    editContact: "Editar contacto",
    deleteContact: "Eliminar contacto",
    importContacts: "Importar contactos",
    exportContacts: "Exportar contactos",
    searchContacts: "Buscar contactos...",
    noContacts: "A\xFAn no hay contactos",
    noContactsDesc: "Agregue contactos para comenzar a enviar mensajes, documentos y formularios.",
    // Fields
    firstName: "Nombre",
    lastName: "Apellido",
    email: "Correo electr\xF3nico",
    phone: "Tel\xE9fono",
    company: "Empresa",
    address: "Direcci\xF3n",
    city: "Ciudad",
    state: "Estado",
    zipCode: "C\xF3digo postal",
    country: "Pa\xEDs",
    timezone: "Zona horaria",
    tags: "Etiquetas",
    // Consent
    consent: "Consentimiento",
    smsOptIn: "Suscripci\xF3n SMS",
    emailOptIn: "Suscripci\xF3n email",
    voiceOptIn: "Suscripci\xF3n voz",
    optedIn: "Suscrito",
    optedOut: "No suscrito",
    consentLevel: "Nivel de consentimiento",
    transactional: "Transaccional",
    informational: "Informativo",
    marketing: "Marketing"
  },
  documents: {
    title: "Documentos",
    document: "Documento",
    documents: "Documentos",
    template: "Plantilla",
    templates: "Plantillas",
    createTemplate: "Crear plantilla",
    editTemplate: "Editar plantilla",
    deleteTemplate: "Eliminar plantilla",
    noDocuments: "A\xFAn no hay documentos",
    noDocumentsDesc: "Cree plantillas de documentos que se pueden enviar para firmas electr\xF3nicas.",
    // Actions
    send: "Enviar para firma",
    resend: "Reenviar",
    remind: "Enviar recordatorio",
    preview: "Vista previa",
    duplicate: "Duplicar",
    archive: "Archivar",
    // Status
    waitingForSignature: "Esperando firma",
    partiallySigned: "Parcialmente firmado",
    fullySigned: "Completamente firmado",
    expiresOn: "Expira el {{date}}"
  },
  forms: {
    title: "Formularios",
    form: "Formulario",
    forms: "Formularios",
    createForm: "Crear formulario",
    editForm: "Editar formulario",
    deleteForm: "Eliminar formulario",
    noForms: "A\xFAn no hay formularios",
    noFormsDesc: "Cree formularios para recopilar informaci\xF3n de sus contactos.",
    // Builder
    formBuilder: "Constructor de formularios",
    addField: "Agregar campo",
    fieldSettings: "Configuraci\xF3n del campo",
    fieldTypes: "Tipos de campo",
    textField: "Campo de texto",
    emailField: "Campo de correo",
    phoneField: "Campo de tel\xE9fono",
    numberField: "Campo num\xE9rico",
    dateField: "Campo de fecha",
    dropdownField: "Desplegable",
    checkboxField: "Casilla de verificaci\xF3n",
    radioField: "Botones de radio",
    fileUpload: "Subir archivo",
    signature: "Firma",
    sectionHeader: "Encabezado de secci\xF3n",
    // Settings
    formSettings: "Configuraci\xF3n del formulario",
    publicUrl: "URL p\xFAblica",
    qrCode: "C\xF3digo QR",
    embedCode: "C\xF3digo de inserci\xF3n",
    submissions: "Env\xEDos",
    viewSubmissions: "Ver env\xEDos"
  },
  messaging: {
    title: "Mensajer\xEDa",
    messages: "Mensajes",
    inbox: "Bandeja de entrada",
    compose: "Redactar",
    send: "Enviar",
    sendNow: "Enviar ahora",
    scheduleFor: "Programar para",
    recipients: "Destinatarios",
    recipient: "Destinatario",
    selectRecipients: "Seleccionar destinatarios",
    // Channels
    sms: "SMS",
    email: "Correo electr\xF3nico",
    voice: "Voz",
    both: "Ambos",
    // Types
    messageType: "Tipo de mensaje",
    transactional: "Transaccional",
    informational: "Informativo",
    marketing: "Marketing",
    // Content
    subject: "Asunto",
    message: "Mensaje",
    template: "Plantilla",
    selectTemplate: "Seleccionar plantilla",
    characterCount: "{{count}} caracteres",
    segments: "{{count}} segmentos",
    // Inbox
    noMessages: "A\xFAn no hay mensajes",
    noMessagesDesc: "Cuando reciba mensajes, aparecer\xE1n aqu\xED.",
    unread: "No le\xEDdo",
    markAsRead: "Marcar como le\xEDdo",
    markAsUnread: "Marcar como no le\xEDdo",
    reply: "Responder",
    assign: "Asignar",
    assignTo: "Asignar a",
    resolve: "Resolver",
    archive: "Archivar"
  },
  activity: {
    title: "Actividad",
    recentActivity: "Actividad reciente",
    allActivity: "Toda la actividad",
    filterByType: "Filtrar por tipo",
    filterByStatus: "Filtrar por estado",
    filterByDate: "Filtrar por fecha",
    noActivity: "A\xFAn no hay actividad",
    noActivityDesc: "Sus env\xEDos y comunicaciones aparecer\xE1n aqu\xED."
  },
  settings: {
    title: "Configuraci\xF3n",
    general: "General",
    account: "Cuenta",
    team: "Equipo",
    billing: "Facturaci\xF3n",
    integrations: "Integraciones",
    notifications: "Notificaciones",
    security: "Seguridad",
    apiKeys: "Claves API",
    webhooks: "Webhooks",
    // Team
    teamMembers: "Miembros del equipo",
    inviteMember: "Invitar miembro",
    role: "Rol",
    admin: "Administrador",
    member: "Miembro",
    pending: "Pendiente",
    resendInvite: "Reenviar invitaci\xF3n",
    removeFromTeam: "Eliminar del equipo"
  },
  auth: {
    signIn: "Iniciar sesi\xF3n",
    signUp: "Registrarse",
    signOut: "Cerrar sesi\xF3n",
    email: "Correo electr\xF3nico",
    password: "Contrase\xF1a",
    confirmPassword: "Confirmar contrase\xF1a",
    forgotPassword: "\xBFOlvid\xF3 su contrase\xF1a?",
    resetPassword: "Restablecer contrase\xF1a",
    createAccount: "Crear cuenta",
    alreadyHaveAccount: "\xBFYa tiene una cuenta?",
    dontHaveAccount: "\xBFNo tiene una cuenta?",
    orContinueWith: "O continuar con",
    termsAgreement: "Al continuar, acepta nuestros {{terms}} y {{privacy}}.",
    termsOfService: "T\xE9rminos de servicio",
    privacyPolicy: "Pol\xEDtica de privacidad"
  },
  tags: {
    title: "Etiquetas",
    tag: "Etiqueta",
    tags: "Etiquetas",
    createTag: "Crear etiqueta",
    editTag: "Editar etiqueta",
    deleteTag: "Eliminar etiqueta",
    tagName: "Nombre de etiqueta",
    tagColor: "Color de etiqueta",
    noTags: "A\xFAn no hay etiquetas",
    noTagsDesc: "Cree etiquetas para organizar sus contactos y contenido.",
    selectTags: "Seleccionar etiquetas",
    addTag: "Agregar etiqueta",
    removeTag: "Quitar etiqueta"
  }
};

// src/i18n/locales/fr.ts
var fr_default = {
  common: {
    // General
    appName: "AORForms",
    loading: "Chargement...",
    save: "Enregistrer",
    cancel: "Annuler",
    delete: "Supprimer",
    edit: "Modifier",
    create: "Cr\xE9er",
    add: "Ajouter",
    remove: "Retirer",
    close: "Fermer",
    confirm: "Confirmer",
    back: "Retour",
    next: "Suivant",
    previous: "Pr\xE9c\xE9dent",
    submit: "Soumettre",
    search: "Rechercher",
    filter: "Filtrer",
    sort: "Trier",
    clear: "Effacer",
    reset: "R\xE9initialiser",
    apply: "Appliquer",
    view: "Voir",
    download: "T\xE9l\xE9charger",
    upload: "T\xE9l\xE9verser",
    export: "Exporter",
    import: "Importer",
    refresh: "Actualiser",
    retry: "R\xE9essayer",
    yes: "Oui",
    no: "Non",
    ok: "OK",
    done: "Termin\xE9",
    select: "S\xE9lectionner",
    selectAll: "Tout s\xE9lectionner",
    deselectAll: "Tout d\xE9s\xE9lectionner",
    noResults: "Aucun r\xE9sultat trouv\xE9",
    showMore: "Afficher plus",
    showLess: "Afficher moins",
    // Navigation
    home: "Accueil",
    dashboard: "Tableau de bord",
    settings: "Param\xE8tres",
    profile: "Profil",
    help: "Aide",
    logout: "D\xE9connexion",
    login: "Connexion",
    signUp: "S'inscrire",
    // Status
    status: "Statut",
    active: "Actif",
    inactive: "Inactif",
    pending: "En attente",
    completed: "Termin\xE9",
    failed: "\xC9chou\xE9",
    draft: "Brouillon",
    sent: "Envoy\xE9",
    delivered: "Livr\xE9",
    opened: "Ouvert",
    clicked: "Cliqu\xE9",
    signed: "Sign\xE9",
    declined: "Refus\xE9",
    expired: "Expir\xE9",
    scheduled: "Programm\xE9",
    processing: "En cours",
    // Time
    today: "Aujourd'hui",
    yesterday: "Hier",
    thisWeek: "Cette semaine",
    thisMonth: "Ce mois",
    lastMonth: "Mois dernier",
    custom: "Personnalis\xE9",
    dateRange: "Plage de dates",
    from: "De",
    to: "\xC0",
    // Pagination
    page: "Page",
    of: "sur",
    showing: "Affichage de",
    results: "r\xE9sultats",
    perPage: "par page",
    firstPage: "Premi\xE8re page",
    lastPage: "Derni\xE8re page",
    nextPage: "Page suivante",
    previousPage: "Page pr\xE9c\xE9dente",
    // Forms
    required: "Requis",
    optional: "Facultatif",
    invalidEmail: "Adresse e-mail invalide",
    invalidPhone: "Num\xE9ro de t\xE9l\xE9phone invalide",
    passwordTooShort: "Le mot de passe doit contenir au moins 8 caract\xE8res",
    fieldRequired: "Ce champ est requis",
    minLength: "Doit contenir au moins {{count}} caract\xE8res",
    maxLength: "Doit contenir au maximum {{count}} caract\xE8res",
    // Errors
    error: "Erreur",
    errorOccurred: "Une erreur s'est produite",
    tryAgain: "Veuillez r\xE9essayer",
    somethingWentWrong: "Quelque chose s'est mal pass\xE9",
    networkError: "Erreur r\xE9seau",
    unauthorized: "Non autoris\xE9",
    forbidden: "Interdit",
    notFound: "Non trouv\xE9",
    serverError: "Erreur serveur",
    // Success
    success: "Succ\xE8s",
    saved: "Enregistr\xE9 avec succ\xE8s",
    deleted: "Supprim\xE9 avec succ\xE8s",
    created: "Cr\xE9\xE9 avec succ\xE8s",
    updated: "Mis \xE0 jour avec succ\xE8s",
    sentSuccessfully: "Envoy\xE9 avec succ\xE8s",
    // Confirmations
    areYouSure: "\xCAtes-vous s\xFBr ?",
    cannotUndo: "Cette action ne peut pas \xEAtre annul\xE9e.",
    deleteConfirm: "\xCAtes-vous s\xFBr de vouloir supprimer ce {{item}} ?"
  },
  contacts: {
    title: "Contacts",
    contact: "Contact",
    contacts: "Contacts",
    addContact: "Ajouter un contact",
    editContact: "Modifier le contact",
    deleteContact: "Supprimer le contact",
    importContacts: "Importer des contacts",
    exportContacts: "Exporter les contacts",
    searchContacts: "Rechercher des contacts...",
    noContacts: "Aucun contact pour le moment",
    noContactsDesc: "Ajoutez des contacts pour commencer \xE0 envoyer des messages, documents et formulaires.",
    // Fields
    firstName: "Pr\xE9nom",
    lastName: "Nom",
    email: "E-mail",
    phone: "T\xE9l\xE9phone",
    company: "Entreprise",
    address: "Adresse",
    city: "Ville",
    state: "\xC9tat/R\xE9gion",
    zipCode: "Code postal",
    country: "Pays",
    timezone: "Fuseau horaire",
    tags: "\xC9tiquettes",
    // Consent
    consent: "Consentement",
    smsOptIn: "Inscription SMS",
    emailOptIn: "Inscription e-mail",
    voiceOptIn: "Inscription vocale",
    optedIn: "Inscrit",
    optedOut: "D\xE9sinscrit",
    consentLevel: "Niveau de consentement",
    transactional: "Transactionnel",
    informational: "Informatif",
    marketing: "Marketing"
  },
  documents: {
    title: "Documents",
    document: "Document",
    documents: "Documents",
    template: "Mod\xE8le",
    templates: "Mod\xE8les",
    createTemplate: "Cr\xE9er un mod\xE8le",
    editTemplate: "Modifier le mod\xE8le",
    deleteTemplate: "Supprimer le mod\xE8le",
    noDocuments: "Aucun document pour le moment",
    noDocumentsDesc: "Cr\xE9ez des mod\xE8les de documents pouvant \xEAtre envoy\xE9s pour signature \xE9lectronique.",
    // Actions
    send: "Envoyer pour signature",
    resend: "Renvoyer",
    remind: "Envoyer un rappel",
    preview: "Aper\xE7u",
    duplicate: "Dupliquer",
    archive: "Archiver",
    // Status
    waitingForSignature: "En attente de signature",
    partiallySigned: "Partiellement sign\xE9",
    fullySigned: "Enti\xE8rement sign\xE9",
    expiresOn: "Expire le {{date}}"
  },
  forms: {
    title: "Formulaires",
    form: "Formulaire",
    forms: "Formulaires",
    createForm: "Cr\xE9er un formulaire",
    editForm: "Modifier le formulaire",
    deleteForm: "Supprimer le formulaire",
    noForms: "Aucun formulaire pour le moment",
    noFormsDesc: "Cr\xE9ez des formulaires pour collecter des informations aupr\xE8s de vos contacts.",
    // Builder
    formBuilder: "Constructeur de formulaires",
    addField: "Ajouter un champ",
    fieldSettings: "Param\xE8tres du champ",
    fieldTypes: "Types de champs",
    textField: "Champ texte",
    emailField: "Champ e-mail",
    phoneField: "Champ t\xE9l\xE9phone",
    numberField: "Champ num\xE9rique",
    dateField: "Champ date",
    dropdownField: "Liste d\xE9roulante",
    checkboxField: "Case \xE0 cocher",
    radioField: "Boutons radio",
    fileUpload: "T\xE9l\xE9versement de fichier",
    signature: "Signature",
    sectionHeader: "En-t\xEAte de section",
    // Settings
    formSettings: "Param\xE8tres du formulaire",
    publicUrl: "URL publique",
    qrCode: "Code QR",
    embedCode: "Code d'int\xE9gration",
    submissions: "Soumissions",
    viewSubmissions: "Voir les soumissions"
  },
  messaging: {
    title: "Messagerie",
    messages: "Messages",
    inbox: "Bo\xEEte de r\xE9ception",
    compose: "R\xE9diger",
    send: "Envoyer",
    sendNow: "Envoyer maintenant",
    scheduleFor: "Programmer pour",
    recipients: "Destinataires",
    recipient: "Destinataire",
    selectRecipients: "S\xE9lectionner les destinataires",
    // Channels
    sms: "SMS",
    email: "E-mail",
    voice: "Voix",
    both: "Les deux",
    // Types
    messageType: "Type de message",
    transactional: "Transactionnel",
    informational: "Informatif",
    marketing: "Marketing",
    // Content
    subject: "Objet",
    message: "Message",
    template: "Mod\xE8le",
    selectTemplate: "S\xE9lectionner un mod\xE8le",
    characterCount: "{{count}} caract\xE8res",
    segments: "{{count}} segments",
    // Inbox
    noMessages: "Aucun message pour le moment",
    noMessagesDesc: "Lorsque vous recevrez des messages, ils appara\xEEtront ici.",
    unread: "Non lu",
    markAsRead: "Marquer comme lu",
    markAsUnread: "Marquer comme non lu",
    reply: "R\xE9pondre",
    assign: "Attribuer",
    assignTo: "Attribuer \xE0",
    resolve: "R\xE9soudre",
    archive: "Archiver"
  },
  activity: {
    title: "Activit\xE9",
    recentActivity: "Activit\xE9 r\xE9cente",
    allActivity: "Toute l'activit\xE9",
    filterByType: "Filtrer par type",
    filterByStatus: "Filtrer par statut",
    filterByDate: "Filtrer par date",
    noActivity: "Aucune activit\xE9 pour le moment",
    noActivityDesc: "Vos envois et communications appara\xEEtront ici."
  },
  settings: {
    title: "Param\xE8tres",
    general: "G\xE9n\xE9ral",
    account: "Compte",
    team: "\xC9quipe",
    billing: "Facturation",
    integrations: "Int\xE9grations",
    notifications: "Notifications",
    security: "S\xE9curit\xE9",
    apiKeys: "Cl\xE9s API",
    webhooks: "Webhooks",
    // Team
    teamMembers: "Membres de l'\xE9quipe",
    inviteMember: "Inviter un membre",
    role: "R\xF4le",
    admin: "Administrateur",
    member: "Membre",
    pending: "En attente",
    resendInvite: "Renvoyer l'invitation",
    removeFromTeam: "Retirer de l'\xE9quipe"
  },
  auth: {
    signIn: "Connexion",
    signUp: "Inscription",
    signOut: "D\xE9connexion",
    email: "E-mail",
    password: "Mot de passe",
    confirmPassword: "Confirmer le mot de passe",
    forgotPassword: "Mot de passe oubli\xE9 ?",
    resetPassword: "R\xE9initialiser le mot de passe",
    createAccount: "Cr\xE9er un compte",
    alreadyHaveAccount: "Vous avez d\xE9j\xE0 un compte ?",
    dontHaveAccount: "Vous n'avez pas de compte ?",
    orContinueWith: "Ou continuer avec",
    termsAgreement: "En continuant, vous acceptez nos {{terms}} et notre {{privacy}}.",
    termsOfService: "Conditions d'utilisation",
    privacyPolicy: "Politique de confidentialit\xE9"
  },
  tags: {
    title: "\xC9tiquettes",
    tag: "\xC9tiquette",
    tags: "\xC9tiquettes",
    createTag: "Cr\xE9er une \xE9tiquette",
    editTag: "Modifier l'\xE9tiquette",
    deleteTag: "Supprimer l'\xE9tiquette",
    tagName: "Nom de l'\xE9tiquette",
    tagColor: "Couleur de l'\xE9tiquette",
    noTags: "Aucune \xE9tiquette pour le moment",
    noTagsDesc: "Cr\xE9ez des \xE9tiquettes pour organiser vos contacts et contenus.",
    selectTags: "S\xE9lectionner des \xE9tiquettes",
    addTag: "Ajouter une \xE9tiquette",
    removeTag: "Retirer l'\xE9tiquette"
  }
};
var defaultNS = "common";
var resources = {
  en: en_default,
  es: es_default,
  fr: fr_default
};
var supportedLanguages = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "es", name: "Spanish", nativeName: "Espa\xF1ol" },
  { code: "fr", name: "French", nativeName: "Fran\xE7ais" }
];
i18n.use(LanguageDetector).use(initReactI18next).init({
  resources,
  defaultNS,
  fallbackLng: "en",
  supportedLngs: ["en", "es", "fr"],
  interpolation: {
    escapeValue: false
    // React already escapes values
  },
  detection: {
    order: ["localStorage", "navigator", "htmlTag"],
    caches: ["localStorage"],
    lookupLocalStorage: "respectly-language"
  }
});
var changeLanguage = (lang) => {
  return i18n.changeLanguage(lang);
};
var getCurrentLanguage = () => {
  return i18n.language;
};
function getAllCountries() {
  return Country.getAllCountries().map((c) => ({
    isoCode: c.isoCode,
    name: c.name,
    phonecode: c.phonecode,
    flag: c.flag
  })).sort((a, b) => a.name.localeCompare(b.name));
}
var PRIORITY_COUNTRIES = ["US", "CA", "GB", "AU"];
function getCountriesWithPriority() {
  const allCountries = getAllCountries();
  const priority = allCountries.filter(
    (c) => PRIORITY_COUNTRIES.includes(c.isoCode)
  );
  const rest = allCountries.filter(
    (c) => !PRIORITY_COUNTRIES.includes(c.isoCode)
  );
  return [...priority, ...rest];
}
function getCountryByCode(isoCode) {
  const country = Country.getCountryByCode(isoCode);
  if (!country) return void 0;
  return {
    isoCode: country.isoCode,
    name: country.name,
    phonecode: country.phonecode,
    flag: country.flag
  };
}
function getStatesByCountry(countryCode) {
  if (!countryCode) return [];
  return State.getStatesOfCountry(countryCode).map((s) => ({
    isoCode: s.isoCode,
    name: s.name,
    countryCode: s.countryCode
  })).sort((a, b) => a.name.localeCompare(b.name));
}
function getStateByCode(countryCode, stateCode) {
  const state = State.getStateByCodeAndCountry(stateCode, countryCode);
  if (!state) return void 0;
  return {
    isoCode: state.isoCode,
    name: state.name,
    countryCode: state.countryCode
  };
}
function countryHasStates(countryCode) {
  if (!countryCode) return false;
  return State.getStatesOfCountry(countryCode).length > 0;
}

// src/location/resolve.ts
var MAX_CANDIDATES = 10;
var COUNTRY_ALIASES = {
  usa: "US",
  "u.s.": "US",
  "u.s.a.": "US",
  "united states of america": "US",
  america: "US",
  uk: "GB",
  "u.k.": "GB",
  "great britain": "GB",
  britain: "GB",
  uae: "AE"
};
function toMatch(opt) {
  return { isoCode: opt.isoCode, name: opt.name };
}
function resolveCountry(input) {
  const raw = (input ?? "").trim();
  if (!raw) return null;
  const q = raw.toLowerCase();
  if (raw.length === 2) {
    const byCode = getCountryByCode(raw.toUpperCase());
    if (byCode) return { match: toMatch(byCode) };
  }
  const aliasCode = COUNTRY_ALIASES[q];
  if (aliasCode) {
    const byAlias = getCountryByCode(aliasCode);
    if (byAlias) return { match: toMatch(byAlias) };
  }
  const all = getAllCountries();
  const exact = all.find((c) => c.name.toLowerCase() === q);
  if (exact) return { match: toMatch(exact) };
  const partial = all.filter((c) => c.name.toLowerCase().includes(q));
  if (partial.length === 1 && partial[0]) return { match: toMatch(partial[0]) };
  if (partial.length > 1) return { candidates: partial.slice(0, MAX_CANDIDATES).map(toMatch) };
  return null;
}
function resolveState(countryCode, input) {
  const country = (countryCode ?? "").trim().toUpperCase();
  const raw = (input ?? "").trim();
  if (!country || !raw) return null;
  const states = getStatesByCountry(country);
  if (states.length === 0) return null;
  const q = raw.toLowerCase();
  const byCode = getStateByCode(country, raw.toUpperCase());
  if (byCode) return { match: toMatch(byCode) };
  const exact = states.find((s) => s.name.toLowerCase() === q);
  if (exact) return { match: toMatch(exact) };
  const partial = states.filter((s) => s.name.toLowerCase().includes(q));
  if (partial.length === 1 && partial[0]) return { match: toMatch(partial[0]) };
  if (partial.length > 1) return { candidates: partial.slice(0, MAX_CANDIDATES).map(toMatch) };
  return null;
}

// src/planFeatures.ts
function parseStoredFeatures(raw) {
  if (raw == null) return { custom: [] };
  if (typeof raw === "string") {
    try {
      return parseStoredFeatures(JSON.parse(raw));
    } catch {
      return { custom: [] };
    }
  }
  if (Array.isArray(raw)) {
    const custom = [];
    for (const item of raw) {
      if (typeof item === "string") {
        const trimmed = item.trim();
        if (trimmed) custom.push({ label: trimmed });
      } else if (item && typeof item === "object") {
        const obj = item;
        if (typeof obj.label === "string" && obj.label.trim()) {
          custom.push({
            label: obj.label.trim(),
            value: typeof obj.value === "string" && obj.value.trim() ? obj.value.trim() : void 0
          });
        }
      }
    }
    return { custom };
  }
  if (typeof raw === "object") {
    const obj = raw;
    const labelOverrides = {};
    if (obj.labelOverrides && typeof obj.labelOverrides === "object") {
      for (const key of [
        "sms",
        "email",
        "ai",
        "users",
        "contacts",
        "forms"
      ]) {
        const v = obj.labelOverrides[key];
        if (typeof v === "string" && v.trim()) {
          labelOverrides[key] = v.trim();
        }
      }
    }
    const custom = [];
    if (Array.isArray(obj.custom)) {
      for (const item of obj.custom) {
        if (item && typeof item === "object") {
          const c = item;
          if (typeof c.label === "string" && c.label.trim()) {
            custom.push({
              label: c.label.trim(),
              value: typeof c.value === "string" && c.value.trim() ? c.value.trim() : void 0
            });
          }
        }
      }
    }
    return {
      custom,
      ...Object.keys(labelOverrides).length ? { labelOverrides } : {}
    };
  }
  return { custom: [] };
}
function stringifyStoredFeatures(stored) {
  const cleanedOverrides = {};
  if (stored.labelOverrides) {
    for (const [k, v] of Object.entries(stored.labelOverrides)) {
      if (typeof v === "string" && v.trim()) {
        cleanedOverrides[k] = v.trim();
      }
    }
  }
  const cleanedCustom = [];
  for (const item of stored.custom || []) {
    if (item && typeof item.label === "string" && item.label.trim()) {
      cleanedCustom.push({
        label: item.label.trim(),
        ...typeof item.value === "string" && item.value.trim() ? { value: item.value.trim() } : {}
      });
    }
  }
  const out = { custom: cleanedCustom };
  if (Object.keys(cleanedOverrides).length) {
    out.labelOverrides = cleanedOverrides;
  }
  return JSON.stringify(out);
}
function toFiniteNumber(value) {
  if (value == null) return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}
function formatOveragePrice(price) {
  const n = toFiniteNumber(price);
  if (n == null || n <= 0) return null;
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}
function overageSuffix(price, template) {
  const formatted = formatOveragePrice(price);
  return formatted ? template(formatted) : "";
}
var CANONICAL_DEFAULT_LABELS = {
  sms: "SMS Messages",
  email: "Email Messages",
  ai: "AI Credits",
  users: "Team Users",
  contacts: "Contacts",
  forms: "Forms"
};
function shouldShowCanonical(key, plan) {
  switch (key) {
    case "sms":
      return (plan.smsCreditsPerMonth ?? 0) > 0 || plan.smsCreditsPerMonth === -1;
    case "email":
      return (plan.emailCreditsPerMonth ?? 0) > 0 || plan.emailCreditsPerMonth === -1;
    case "ai":
      return (plan.aiCreditsPerMonth ?? 0) > 0 || plan.aiCreditsPerMonth === -1;
    case "users":
      return (plan.maxUsers ?? 0) > 0 || plan.maxUsers === -1;
    case "contacts":
      return (plan.maxContacts ?? 0) > 0 || plan.maxContacts === -1;
    case "forms":
      return (plan.maxForms ?? 0) > 0 || plan.maxForms === -1;
  }
}
function formatCanonicalValue(key, plan) {
  if (!shouldShowCanonical(key, plan)) return null;
  switch (key) {
    case "sms": {
      const limit = plan.smsCreditsPerMonth;
      const base = limit === -1 ? "Unlimited" : `${limit.toLocaleString()} per month`;
      return base + overageSuffix(plan.smsOveragePrice, (f) => ` (${f} per additional)`);
    }
    case "email": {
      const limit = plan.emailCreditsPerMonth;
      const base = limit === -1 ? "Unlimited" : `${limit.toLocaleString()} per month`;
      return base + overageSuffix(plan.emailOveragePrice, (f) => ` (${f} per additional)`);
    }
    case "ai": {
      const limit = plan.aiCreditsPerMonth;
      const base = limit === -1 ? "Unlimited" : `${limit.toLocaleString()} credits per month`;
      return base + overageSuffix(plan.aiCreditOveragePrice, (f) => ` (${f} per additional credit)`);
    }
    case "users": {
      const limit = plan.maxUsers;
      const base = limit === -1 ? "Unlimited" : `Up to ${limit}`;
      return base + overageSuffix(plan.userOveragePrice, (f) => ` (${f}/user/month for additional)`);
    }
    case "contacts": {
      const limit = plan.maxContacts;
      return limit === -1 ? "Unlimited" : `${limit.toLocaleString()} max`;
    }
    case "forms": {
      const limit = plan.maxForms;
      return limit === -1 ? "Unlimited" : `${limit.toLocaleString()} max`;
    }
  }
}
var CANONICAL_CATEGORY = {
  sms: "messaging",
  email: "messaging",
  ai: "messaging",
  users: "team",
  contacts: "storage",
  forms: "storage"
};
var CANONICAL_TYPE = {
  sms: "limit",
  email: "limit",
  ai: "limit",
  users: "limit",
  contacts: "limit",
  forms: "limit"
};
function buildPlanFeatures(plan) {
  const features = [];
  const stored = parseStoredFeatures(plan.features);
  const overrides = stored.labelOverrides ?? {};
  const canonicalOrder = [
    "sms",
    "email",
    "ai",
    "users",
    "contacts",
    "forms"
  ];
  for (const key of canonicalOrder) {
    const value = formatCanonicalValue(key, plan);
    if (value == null) continue;
    features.push({
      label: overrides[key] || CANONICAL_DEFAULT_LABELS[key],
      category: CANONICAL_CATEGORY[key],
      type: CANONICAL_TYPE[key],
      value
    });
  }
  for (const item of stored.custom) {
    features.push({
      label: item.label,
      category: "integrations",
      type: "boolean",
      ...item.value ? { value: item.value } : {}
    });
  }
  return features;
}

// src/billing/currency.ts
function currencyForCountry(country) {
  return country?.trim().toUpperCase() === "CA" ? "CAD" : "USD";
}
function isBillingCurrency(value) {
  return value === "CAD" || value === "USD";
}

// src/tenant/tenDlcReadiness.ts
var FIELD_LABELS = {
  businessId: "EIN",
  businessWebsite: "Website",
  billingStreet1: "Street",
  billingCity: "City",
  billingProvince: "State / Province",
  billingPostalCode: "Postal code",
  billingCountry: "Country"
};
var REQUIRED_KEYS = [
  "businessId",
  "businessWebsite",
  "billingStreet1",
  "billingCity",
  "billingProvince",
  "billingPostalCode",
  "billingCountry"
];
function isFilled(value) {
  return typeof value === "string" && value.trim().length > 0;
}
function checkTenDlcReadiness(tenant) {
  if (!tenant) {
    return {
      ready: false,
      individualBlocked: false,
      missing: REQUIRED_KEYS.map((k) => FIELD_LABELS[k]),
      missingKeys: [...REQUIRED_KEYS]
    };
  }
  if (tenant.businessType === "individual") {
    return { ready: false, individualBlocked: true, missing: [], missingKeys: [] };
  }
  const missingKeys = REQUIRED_KEYS.filter((key) => !isFilled(tenant[key]));
  return {
    ready: missingKeys.length === 0,
    individualBlocked: false,
    missing: missingKeys.map((k) => FIELD_LABELS[k]),
    missingKeys
  };
}
function getTenDlcFieldLabel(key) {
  return FIELD_LABELS[key];
}

// src/internalDnc.ts
function extractUsAreaCode(e164) {
  if (!e164.startsWith("+1")) return null;
  const digits = e164.slice(2).replace(/\D/g, "");
  if (digits.length < 10) return null;
  return digits.slice(0, 3);
}
function matchesInternalDncRules(e164Phone, rules) {
  if (!e164Phone) return { suppressed: false };
  const phone = e164Phone.trim();
  if (!phone) return { suppressed: false };
  for (const exact of rules.exactNumbers) {
    if (exact === phone) {
      return {
        suppressed: true,
        reason: `Suppressed: exact match ${exact}`,
        ruleType: "exactNumber",
        matched: exact
      };
    }
  }
  let bestPrefix = null;
  for (const prefix of rules.prefixes) {
    if (prefix && phone.startsWith(prefix)) {
      if (bestPrefix === null || prefix.length > bestPrefix.length) {
        bestPrefix = prefix;
      }
    }
  }
  if (bestPrefix !== null) {
    return {
      suppressed: true,
      reason: `Suppressed: prefix ${bestPrefix}`,
      ruleType: "prefix",
      matched: bestPrefix
    };
  }
  const areaCode = extractUsAreaCode(phone);
  if (areaCode) {
    for (const ac of rules.areaCodes) {
      if (ac === areaCode) {
        return {
          suppressed: true,
          reason: `Suppressed: ${ac} area code`,
          ruleType: "areaCode",
          matched: ac
        };
      }
    }
  }
  return { suppressed: false };
}
function isValidAreaCode(value) {
  return /^\d{3}$/.test(value);
}
function isValidPrefix(value) {
  return /^\+\d{1,15}$/.test(value);
}

// src/pre/renderer.ts
function substituteParams(template, params) {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    const value = params[key];
    if (value === void 0 || value === null) return match;
    return String(value);
  });
}
function renderRequirement(rule, check, definitions) {
  const definition = definitions.get(check.checkId);
  const citation = rule.userFacingCitation ?? rule.sourceRef ?? null;
  const bucket = normalizeBucket(definition?.ruleType, check.checkId);
  if (rule.userFacingSummary && rule.userFacingSummary.trim().length > 0) {
    return { text: rule.userFacingSummary, citation, bucket };
  }
  if (definition?.userFacingTemplate && definition.userFacingTemplate.trim().length > 0) {
    return {
      text: substituteParams(definition.userFacingTemplate, check.params ?? null),
      citation,
      bucket
    };
  }
  if (rule.description && rule.description.trim().length > 0) {
    return { text: rule.description, citation, bucket };
  }
  return { text: check.checkId, citation, bucket };
}
function renderRule(rule, definitions) {
  if (rule.userFacingSummary && rule.userFacingSummary.trim().length > 0) {
    const firstCheck = rule.requiredChecks[0];
    const bucket = firstCheck ? normalizeBucket(definitions.get(firstCheck.checkId)?.ruleType, firstCheck.checkId) : inferBucket(rule);
    return [
      {
        text: rule.userFacingSummary,
        citation: rule.userFacingCitation ?? rule.sourceRef ?? null,
        bucket
      }
    ];
  }
  if (rule.requiredChecks && rule.requiredChecks.length > 0) {
    return rule.requiredChecks.map((check) => renderRequirement(rule, check, definitions));
  }
  if (rule.description && rule.description.trim().length > 0) {
    return [
      {
        text: rule.description,
        citation: rule.userFacingCitation ?? rule.sourceRef ?? null,
        bucket: inferBucket(rule)
      }
    ];
  }
  return [];
}
var OPT_OUT_KEYWORDS = [
  "opt-out",
  "opt out",
  "optout",
  "unsubscribe",
  "stop keyword",
  "\\bstop\\b",
  "dnc",
  "do[- ]not[- ]call",
  "do[- ]not[- ]contact",
  "dncl",
  "wireless dnc",
  "reassigned numbers",
  "rnd",
  "revoke",
  "withdrawal of consent"
];
var OPT_IN_KEYWORDS = [
  "consent",
  "opt-in",
  "opt in",
  "optin",
  "express written",
  "implied consent",
  "prior consent",
  "eligibility",
  "b2b exemption",
  "business contact",
  "enrollment",
  "subscribe"
];
function matchesAny(haystack, patterns) {
  for (const p of patterns) {
    if (new RegExp(`(^|\\W)(${p})(\\W|$)`, "i").test(haystack)) return true;
  }
  return false;
}
function inferBucket(rule) {
  const haystack = [rule.name, rule.userFacingSummary, rule.description].filter(Boolean).join(" \n ");
  if (haystack) {
    if (matchesAny(haystack, OPT_OUT_KEYWORDS)) return "OPT_OUT";
    if (matchesAny(haystack, OPT_IN_KEYWORDS)) return "OPT_IN";
  }
  return bucketFromLayer(rule.layer);
}
function bucketFromLayer(layer) {
  if (layer === "constraint") return "SENDING";
  return "OPT_IN";
}
var LEGACY_CHECK_BUCKETS = {
  // Opt-in / consent / eligibility
  CHK_CONTACT_ELIGIBLE: "OPT_IN",
  CHK_CONSENT_AT_LEAST: "OPT_IN",
  CHK_WIRELESS_MARKETING_CONSENT_OK: "OPT_IN",
  CHK_CASL_CONSENT_VALID: "OPT_IN",
  CHK_CASL_CONSPICUOUS_PUBLICATION_VALID: "OPT_IN",
  CHK_LGPD_LAWFUL_BASIS_VALID: "OPT_IN",
  CHK_CHANNEL_PREFERENCE_MATCH: "OPT_IN",
  // Opt-out / DNC / revocation
  CHK_INTERNAL_DNC_CLEAR: "OPT_OUT",
  CHK_NATIONAL_REGISTRY_CLEAR: "OPT_OUT",
  CHK_BRAZIL_NAO_ME_PERTURBE_CLEAR: "OPT_OUT",
  CHK_REVOCATION_HANDLING_ENABLED: "OPT_OUT",
  CHK_END_CALL_WITHIN_10S_ON_REQUEST: "OPT_OUT",
  CHK_DNC_DISCLOSURE_AT_CALL_START: "OPT_OUT",
  CHK_SENDER_ID_AND_UNSUB_PRESENT: "OPT_OUT",
  CHK_RIGHT_TO_DISCONNECT_OK: "OPT_OUT",
  // Sending mechanics
  CHK_NATIONAL_REGISTRY_SCRUBBED: "SENDING",
  CHK_WITHIN_CALLING_HOURS: "SENDING",
  CHK_FREQUENCY_CAP_24H_OK: "SENDING",
  CHK_FREQUENCY_CAP_7D_OK: "SENDING",
  CHK_BRAZIL_0303_PREFIX: "SENDING",
  CHK_AUTOMATION_ALLOWED_FOR_SEND: "SENDING"
};
function normalizeBucket(raw, checkId) {
  if (raw === "OPT_IN" || raw === "OPT_OUT" || raw === "SENDING") return raw;
  const legacy = checkId ? LEGACY_CHECK_BUCKETS[checkId] : void 0;
  if (legacy) return legacy;
  if (raw === "SEND") return "SENDING";
  return "OPT_IN";
}

// src/pre/eligibility.ts
var DELIVERY_METHOD_CHANNELS = {
  sms: ["sms"],
  email: ["email"],
  voice: ["voice"],
  sms_email: ["sms", "email"]
};
var CHANNEL_LABEL = {
  sms: "SMS",
  email: "email",
  voice: "voice calls"
};
var EMPTY_CONSENT = {
  sms: { informational: false, marketing: false },
  email: { informational: false, marketing: false },
  voice: { informational: false, marketing: false }
};
function blankConsentMatrix() {
  return {
    sms: { ...EMPTY_CONSENT.sms },
    email: { ...EMPTY_CONSENT.email },
    voice: { ...EMPTY_CONSENT.voice }
  };
}
function hasAnyGranularGrant(consent) {
  if (!consent) return false;
  return consent.sms.informational || consent.sms.marketing || consent.email.informational || consent.email.marketing || consent.voice.informational || consent.voice.marketing;
}
function hasChannelAddress(contact, channel) {
  return channel === "email" ? Boolean(contact.email) : Boolean(contact.phone);
}
function optInFor(contact, channel) {
  if (channel === "sms") return contact.smsOptIn;
  if (channel === "email") return contact.emailOptIn;
  return contact.voiceOptIn;
}
function evaluateChannelEligibility(contact, channel, messageType) {
  if (channel !== "sms" && channel !== "email" && channel !== "voice") {
    return {
      eligible: false,
      reason: "UNKNOWN_CHANNEL",
      source: "unknown",
      message: `Unknown channel: ${String(channel)}`
    };
  }
  if (contact.isNew === true) {
    return {
      eligible: true,
      reason: "ELIGIBLE_NEW_CONTACT",
      source: "new-contact",
      message: "New contact \u2014 assumed eligible"
    };
  }
  if (!hasChannelAddress(contact, channel)) {
    return channel === "email" ? { eligible: false, reason: "MISSING_EMAIL", source: "contact-info", message: "Missing email address" } : { eligible: false, reason: "MISSING_PHONE", source: "contact-info", message: "Missing phone number" };
  }
  if (messageType === "transactional") {
    return {
      eligible: true,
      reason: "ELIGIBLE_TRANSACTIONAL",
      source: "transactional",
      message: "Transactional \u2014 no consent required"
    };
  }
  const consent = contact.consent;
  const useGranular = consent != null && (contact.hasGranularConsentData ?? hasAnyGranularGrant(consent));
  if (useGranular && consent) {
    const flags = consent[channel];
    const granted = messageType === "marketing" ? flags.marketing : flags.informational || flags.marketing;
    return granted ? { eligible: true, reason: "ELIGIBLE", source: "granular", message: "Consent on record" } : {
      eligible: false,
      reason: "NO_GRANULAR_CONSENT",
      source: "granular",
      message: `No ${messageType} consent on record for ${CHANNEL_LABEL[channel]}`
    };
  }
  const optedIn = optInFor(contact, channel);
  if (optedIn === void 0 || optedIn === null) {
    return { eligible: true, reason: "ELIGIBLE", source: "legacy", message: "No opt-in data \u2014 assumed eligible" };
  }
  if (!optedIn) {
    return {
      eligible: false,
      reason: "NOT_OPTED_IN",
      source: "legacy",
      message: `Has not opted in for ${CHANNEL_LABEL[channel]}`
    };
  }
  const consentLevel = contact.consentLevel;
  if (!consentLevel) {
    return { eligible: true, reason: "ELIGIBLE", source: "legacy", message: "Opted in" };
  }
  const satisfied = messageType === "marketing" ? consentLevel === "marketing" : consentLevel === "informational" || consentLevel === "marketing";
  return satisfied ? { eligible: true, reason: "ELIGIBLE", source: "legacy", message: "Opted in" } : {
    eligible: false,
    reason: "CONSENT_LEVEL_INSUFFICIENT",
    source: "legacy",
    message: `Consent level '${consentLevel}' insufficient for ${messageType} ${CHANNEL_LABEL[channel]}`
  };
}
function evaluateMultiChannelEligibility(contact, channels, messageType) {
  const perChannel = {};
  const eligibleChannels = [];
  const blocked = [];
  for (const channel of channels) {
    const result = evaluateChannelEligibility(contact, channel, messageType);
    perChannel[channel] = result;
    if (result.eligible) eligibleChannels.push(channel);
    else blocked.push(result);
  }
  const eligible = eligibleChannels.length > 0;
  return {
    eligible,
    perChannel,
    blockedReasons: eligible ? [] : blocked,
    eligibleChannels
  };
}
function isEligibleForAnyPurpose(contact, channel) {
  return evaluateChannelEligibility(contact, channel, "informational").eligible || evaluateChannelEligibility(contact, channel, "marketing").eligible;
}
function isPurposeGranted(contact, channel, purpose) {
  const consent = contact.consent;
  const useGranular = consent != null && (contact.hasGranularConsentData ?? hasAnyGranularGrant(consent));
  if (useGranular && consent) {
    return consent[channel][purpose];
  }
  const optedIn = optInFor(contact, channel);
  if (!optedIn) return false;
  if (purpose === "marketing") return contact.consentLevel === "marketing";
  return true;
}
function describeEligibility(contactName, result) {
  const name = contactName.trim() || "Unknown contact";
  return `${name}: ${result.message}`;
}
function consentMatrixFromEligibilityFlags(flags) {
  if (!flags) return null;
  return {
    sms: { informational: flags.informationalSms, marketing: flags.marketingSms },
    email: { informational: flags.informationalEmail, marketing: flags.marketingEmail },
    voice: { informational: flags.informationalVoice, marketing: flags.marketingVoice }
  };
}
var EXPRESS_STATUSES = /* @__PURE__ */ new Set(["EXPRESS_OPTIN", "WRITTEN_EXPRESS_OPTIN"]);
function consentMatrixFromConsentRecords(records) {
  const matrix = blankConsentMatrix();
  for (const record of records) {
    if (!EXPRESS_STATUSES.has(record.status)) continue;
    const channel = record.channel.toLowerCase();
    const purpose = record.purpose.toLowerCase();
    if (channel !== "sms" && channel !== "email" && channel !== "voice") continue;
    if (purpose !== "informational" && purpose !== "marketing") continue;
    matrix[channel][purpose] = true;
  }
  return matrix;
}

// src/pre/consent-mirror.ts
var CONSENT_LEVELS = {
  UNKNOWN: 0,
  NONE: 1,
  OPTOUT: 2,
  // IMPLICIT_OPT_IN counts as a grant only while unexpired. Callers must apply
  // the expiry downgrade before consulting this map — `effectiveConsentStatus`
  // below does it for you.
  IMPLICIT_OPT_IN: 3,
  EXPRESS_OPTIN: 3,
  WRITTEN_EXPRESS_OPTIN: 4
};
var GRANT_THRESHOLD = CONSENT_LEVELS.EXPRESS_OPTIN;
function effectiveConsentStatus(record, now = /* @__PURE__ */ new Date()) {
  if (record.status === "IMPLICIT_OPT_IN" && record.expiresAt && record.expiresAt.getTime() <= now.getTime()) {
    return "NONE";
  }
  return record.status;
}
function isGrantLevel(status) {
  return (CONSENT_LEVELS[status] ?? 0) >= GRANT_THRESHOLD;
}
function normalizeChannel(channel) {
  const lower = channel.toLowerCase();
  return lower === "sms" || lower === "email" || lower === "voice" ? lower : null;
}
function normalizePurpose(purpose) {
  const lower = purpose.toLowerCase();
  return lower === "informational" || lower === "marketing" ? lower : null;
}
function effectiveConsentMatrix(records, now = /* @__PURE__ */ new Date()) {
  const matrix = blankConsentMatrix();
  for (const record of records) {
    const channel = normalizeChannel(record.channel);
    const purpose = normalizePurpose(record.purpose);
    if (!channel || !purpose) continue;
    if (isGrantLevel(effectiveConsentStatus(record, now))) {
      matrix[channel][purpose] = true;
    }
  }
  return matrix;
}
function channelsWithConsentRecords(records) {
  const channels = /* @__PURE__ */ new Set();
  for (const record of records) {
    const channel = normalizeChannel(record.channel);
    if (channel) channels.add(channel);
  }
  return channels;
}
function deriveConsentLevel(matrix) {
  const channels = ["sms", "email", "voice"];
  if (channels.some((c) => matrix[c].marketing)) return "marketing";
  if (channels.some((c) => matrix[c].informational)) return "informational";
  return "transactional";
}
function deriveLegacyMirror(matrix) {
  const channels = ["sms", "email", "voice"];
  const optIn = (c) => matrix[c].informational || matrix[c].marketing;
  return {
    smsOptIn: optIn("sms"),
    emailOptIn: optIn("email"),
    voiceOptIn: optIn("voice"),
    consentLevel: deriveConsentLevel(matrix),
    grantedChannels: channels.filter(optIn),
    ungrantedChannels: channels.filter((c) => !optIn(c))
  };
}
var LEVEL_RANK = {
  transactional: 0,
  informational: 1,
  marketing: 2
};
function isConsentLevelUpgrade(current, next) {
  const currentRank = LEVEL_RANK[current ?? "transactional"] ?? 0;
  return LEVEL_RANK[next] > currentRank;
}

// src/pre/eligibility-flags.ts
var CHANNELS = ["SMS", "EMAIL", "VOICE"];
var PURPOSES = ["INFORMATIONAL", "MARKETING"];
var FLAG_KEYS = {
  INFORMATIONAL_SMS: "informationalSms",
  INFORMATIONAL_EMAIL: "informationalEmail",
  INFORMATIONAL_VOICE: "informationalVoice",
  MARKETING_SMS: "marketingSms",
  MARKETING_EMAIL: "marketingEmail",
  MARKETING_VOICE: "marketingVoice"
};
function eligibilityFlagKey(channel, purpose) {
  return FLAG_KEYS[`${purpose}_${channel}`];
}
function purposeSatisfiedBy(purpose) {
  return purpose === "INFORMATIONAL" ? ["INFORMATIONAL", "MARKETING"] : ["MARKETING"];
}
function normalizeChannel2(channel) {
  const upper = channel.toUpperCase();
  return upper === "SMS" || upper === "EMAIL" || upper === "VOICE" ? upper : null;
}
function normalizePurpose2(purpose) {
  const upper = purpose.toUpperCase();
  return upper === "INFORMATIONAL" || upper === "MARKETING" ? upper : null;
}
function legacyOptInFor(legacy, channel) {
  if (channel === "SMS") return legacy.smsOptIn;
  if (channel === "EMAIL") return legacy.emailOptIn;
  return legacy.voiceOptIn;
}
function legacyOptOutAtFor(legacy, channel) {
  if (channel === "SMS") return legacy.smsOptOutAt;
  if (channel === "EMAIL") return legacy.emailOptOutAt;
  return null;
}
function computeEligibilityFlagsPure(input) {
  const now = input.now ?? /* @__PURE__ */ new Date();
  const consentLevelByCell = {};
  const channelsWithRecords = /* @__PURE__ */ new Set();
  for (const record of input.consentRecords) {
    const channel = normalizeChannel2(record.channel);
    const purpose = normalizePurpose2(record.purpose);
    if (!channel || !purpose) continue;
    channelsWithRecords.add(channel);
    const level = CONSENT_LEVELS[effectiveConsentStatus(record, now)] ?? 0;
    const key = `${channel}_${purpose}`;
    if (level > (consentLevelByCell[key] ?? 0)) consentLevelByCell[key] = level;
  }
  const flags = {
    informationalSms: false,
    informationalEmail: false,
    informationalVoice: false,
    marketingSms: false,
    marketingEmail: false,
    marketingVoice: false
  };
  for (const channel of CHANNELS) {
    const onDnc = input.dncChannels.has(channel);
    const legacy = input.legacy;
    const fallbackDisabled = !legacy || channelsWithRecords.has(channel) || legacyOptOutAtFor(legacy, channel) !== null;
    const fallbackLevel = !fallbackDisabled && legacy && legacyOptInFor(legacy, channel) ? GRANT_THRESHOLD : 0;
    for (const purpose of PURPOSES) {
      const cellLevel = consentLevelByCell[`${channel}_${purpose}`];
      const level = cellLevel === void 0 ? fallbackLevel : cellLevel;
      flags[eligibilityFlagKey(channel, purpose)] = level >= GRANT_THRESHOLD && !onDnc;
    }
  }
  return flags;
}

// src/pre/sequence-eligibility.ts
function evaluateSequenceSendEligibility(contact, channel, messageType, now = /* @__PURE__ */ new Date()) {
  const matrix = effectiveConsentMatrix(contact.consentRecords ?? [], now);
  return evaluateChannelEligibility(
    {
      ...contact,
      consent: matrix,
      hasGranularConsentData: true
    },
    channel,
    messageType
  );
}

// src/recaptcha.ts
var SCRIPT_ATTR = "data-respectly-recaptcha";
var scriptPromise = null;
var warnedMissingKey = false;
function getSiteKey() {
  if (typeof import.meta === "undefined" || !import.meta.env) return void 0;
  const key = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  return typeof key === "string" && key.length > 0 ? key : void 0;
}
function loadScript(siteKey) {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("recaptcha: not in a browser environment"));
      return;
    }
    const existing = document.querySelector(`script[${SCRIPT_ATTR}]`);
    if (existing) {
      if (window.grecaptcha?.enterprise) {
        resolve();
      } else {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error("recaptcha: script failed to load")), { once: true });
      }
      return;
    }
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.setAttribute(SCRIPT_ATTR, "");
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(new Error("recaptcha: script failed to load")), { once: true });
    document.head.appendChild(script);
  });
  return scriptPromise;
}
async function executeRecaptcha(action) {
  const siteKey = getSiteKey();
  if (!siteKey) {
    if (!warnedMissingKey) {
      warnedMissingKey = true;
      console.warn("[recaptcha] VITE_RECAPTCHA_SITE_KEY not set \u2014 sending empty tokens");
    }
    return "";
  }
  try {
    await loadScript(siteKey);
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha?.enterprise) {
      console.warn("[recaptcha] grecaptcha.enterprise not available after script load");
      return "";
    }
    return await new Promise((resolve) => {
      grecaptcha.enterprise.ready(() => {
        grecaptcha.enterprise.execute(siteKey, { action }).then(resolve).catch((err) => {
          console.warn("[recaptcha] execute failed:", err);
          resolve("");
        });
      });
    });
  } catch (err) {
    console.warn("[recaptcha] token generation failed:", err);
    return "";
  }
}

export { ANIMATION, BREAKPOINTS, CANONICAL_DEFAULT_LABELS, COLORS, CONSENT_LEVELS, DELIVERY_METHODS, DELIVERY_METHOD_CHANNELS, FILE_LIMITS, GRANT_THRESHOLD, LANGUAGE_NAMES, PAGINATION, RATE_LIMITS, SEND_TYPES, SMS_LIMITS, STATUS_COLORS, STORAGE_KEYS, SUPPORTED_LANGUAGES, TAG_COLORS, US_TIMEZONES, blankConsentMatrix, buildCampaignName, buildPlanFeatures, calculateSmsSegments, changeLanguage, channelsWithConsentRecords, checkTenDlcReadiness, computeEligibilityFlagsPure, consentMatrixFromConsentRecords, consentMatrixFromEligibilityFlags, containsOptInKeyword, containsOptOutKeyword, countryHasStates, currencyForCountry, defaultNS, deriveConsentLevel, deriveLegacyMirror, describeEligibility, effectiveConsentMatrix, effectiveConsentStatus, eligibilityFlagKey, evaluateChannelEligibility, evaluateMultiChannelEligibility, evaluateSequenceSendEligibility, executeRecaptcha, formatCanonicalValue, formatCurrency, formatDate, formatDateTime, formatE164, formatFileSize, formatFullName, formatNumber, formatPercentage, formatPhoneNumber, formatRelativeTime, getAllCountries, getCountriesWithPriority, getCountryByCode, getCurrentLanguage, getInitials, getStateByCode, getStatesByCountry, getTenDlcFieldLabel, hasAnyGranularGrant, isBillingCurrency, isConsentLevelUpgrade, isEligibleForAnyPurpose, isGrantLevel, isPurposeGranted, isValidAreaCode, isValidEmail, isValidHexColor, isValidPhoneNumber, isValidPostalCode, isValidPrefix, isValidUrl, isValidUuid, isWithinTcpaHours, matchesInternalDncRules, normalizeToE164, parseStoredFeatures, phoneMatchVariants, purposeSatisfiedBy, renderRequirement, renderRule, resolveCountry, resolveState, resources, sanitizeHtml, shouldShowCanonical, slugify, storedPhoneVariants, stringifyStoredFeatures, supportedLanguages, toSmsRoutingKey, toStoredPhone, truncate, validatePasswordStrength };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map